import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

/** Un número de página o un separador "…" (paginador compacto con muchas páginas). */
interface PagePipItem {
  value: number;
  ellipsis: boolean;
}

/**
 * Paginador reutilizable de las tablas del flujo "Solicitud de Pipas"
 * (Solicitudes y Cronograma, ambas en pipes.component). Puramente presentacional:
 * recibe la página actual (1-based) y el total de páginas, y emite la página
 * elegida. SIEMPRE se muestra, incluso con una sola página — en ese caso ‹ y ›
 * quedan deshabilitados y solo se ve "1" (así el layout de la tabla no "salta"
 * al pasar de 1 a varias páginas).
 */
@Component({
  selector: 'app-pipes-paginator',
  templateUrl: './pipes-paginator.component.html',
  styleUrls: ['./pipes-paginator.component.scss'],
})
export class PipesPaginatorComponent implements OnChanges {
  @Input() page = 1;
  @Input() totalPages = 1;
  @Output() pageChange = new EventEmitter<number>();

  /**
   * Números de página a mostrar, CACHEADOS en un campo (fix 2026-07-10: antes
   * era un getter que devolvía objetos nuevos en cada ciclo de change
   * detection; sin trackBy el *ngFor destruía y recreaba los <button> en cada
   * CD —p.ej. un hover disparaba una transición CSS→CD— y el click se perdía
   * a mitad de la interacción porque el botón bajo el mouse ya no era el
   * mismo nodo DOM). Se recalcula solo cuando cambian los @Input relevantes
   * (ver ngOnChanges). Inicializado también en la declaración para cubrir el
   * caso default page=1/totalPages=1 sin binding explícito, donde Angular
   * podría no disparar ngOnChanges antes del primer render.
   */
  pageItems: PagePipItem[] = this.buildPageItems();

  get canPrev(): boolean {
    return this.page > 1;
  }

  get canNext(): boolean {
    return this.page < this.totalPages;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.pageItems = this.buildPageItems();
  }

  /**
   * Números de página a mostrar. Con pocas páginas (≤7) se listan todas; con
   * más, se compacta a primera + rango alrededor de la actual + última, con un
   * "…" en los huecos (patrón estándar de paginador, evita una fila interminable
   * de números cuando hay muchas páginas).
   */
  private buildPageItems(): PagePipItem[] {
    const total = this.totalPages;
    const nums: number[] = [];
    if (total <= 7) {
      for (let p = 1; p <= total; p++) { nums.push(p); }
    } else {
      nums.push(1);
      const start = Math.max(2, this.page - 1);
      const end = Math.min(total - 1, this.page + 1);
      if (start > 2) { nums.push(-1); }
      for (let p = start; p <= end; p++) { nums.push(p); }
      if (end < total - 1) { nums.push(-1); }
      nums.push(total);
    }
    return nums.map((n) => ({ value: n, ellipsis: n === -1 }));
  }

  /** trackBy del *ngFor de botones numéricos: identidad estable por valor de
   * página (o índice para los separadores "…"), en lugar del objeto nuevo que
   * antes generaba el getter en cada CD (ver comentario de pageItems arriba). */
  trackByItem(index: number, item: PagePipItem): string {
    return item.ellipsis ? 'e' + index : 'p' + item.value;
  }

  goTo(p: number): void {
    if (p === this.page || p < 1 || p > this.totalPages) { return; }
    this.pageChange.emit(p);
  }

  prev(): void {
    if (this.canPrev) { this.pageChange.emit(this.page - 1); }
  }

  next(): void {
    if (this.canNext) { this.pageChange.emit(this.page + 1); }
  }
}
